import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCardIcon from '@mui/icons-material/AddCard';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import useFeedbackAlert from './common/useFeedbackAlert';
import { getAuthPermissoes, getAuthUserId } from '../services/authStorage';
import { useEstabelecimento } from '../context/EstabelecimentoContext';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../utils/reportBranding';

import {
  createConvenioRecebimento,
  deleteConvenioRecebimento,
  enviarConvenioExtratoWhatsApp,
  getAllClients,
  getAllPaymentMethods,
  getClientById,
  getConvenioExtrato,
  getConvenioMovimentos,
  getConvenioSaldo,
  getSessoesCaixa,
  getWhatsAppSessao,
} from '../services/api';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR');
};

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDataIso = (valor) => {
  const [y, m, d] = String(valor || '').slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : '';
};

const periodoPreset = (preset, deCustom, ateCustom) => {
  if (preset === 'custom') return { de: deCustom, ate: ateCustom };
  const fim = new Date();
  const inicio = new Date();
  if (preset === '30') inicio.setDate(inicio.getDate() - 29);
  else if (preset === '90') inicio.setMonth(inicio.getMonth() - 3);
  else inicio.setFullYear(inicio.getFullYear() - 1);
  return { de: isoDate(inicio), ate: isoDate(fim) };
};

const tipoExtrato = (tipo) => {
  if (tipo === 'L') return 'Lançamento';
  if (tipo === 'C') return 'Cashback';
  return 'Recebimento';
};

function imprimirExtrato(popup, { company, extrato, de, ate }) {
  const rows = (extrato.itens || []).map((i) => `<tr>
    <td>${html(fmtDate(i.data))}</td>
    <td>${html(tipoExtrato(i.tipo))}</td>
    <td>${html(i.descricao)}</td>
    <td class="right">${i.debito ? html(money(i.debito)) : ''}</td>
    <td class="right">${i.credito ? html(money(i.credito)) : ''}</td>
    <td class="right">${html(money(i.saldo))}</td>
  </tr>`).join('');
  const doc = extrato.documento ? ` · ${extrato.documento}` : '';
  popup.document.open();
  popup.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>Extrato de convênio</title>
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #172033; font: 11px Arial, sans-serif; }
      header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #0495f0; padding-bottom: 8px; gap: 16px; }
      h1 { margin: 2px 0 0; font-size: 20px; }
      ${REPORT_BRAND_CSS}
      .period { color: #5f6878; text-align: right; }
      .summary { display: flex; gap: 8px; margin: 12px 0; flex-wrap: wrap; }
      .summary div { min-width: 120px; border: 1px solid #d8dde7; border-radius: 5px; padding: 7px 9px; }
      .summary span, .summary strong { display: block; }
      .summary span { color: #687386; font-size: 9px; }
      .summary strong { margin-top: 2px; font-size: 13px; }
      table { width: 100%; border-collapse: collapse; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; page-break-inside: avoid; }
      th { background: #eaf6fe; color: #172033; text-align: left; padding: 6px; border-bottom: 1px solid #9fcfec; }
      td { padding: 5px 6px; border-bottom: 1px solid #e2e6ed; vertical-align: top; }
      td.right, th.right { text-align: right; }
      footer { position: fixed; bottom: -7mm; right: 0; color: #737c8c; font-size: 8px; }
      @media screen { body { padding: 18px; } }
    </style></head><body>
    <header>
      ${reportBrandHtml(company, { title: 'Extrato de convênio' })}
      <div class="period">${html(extrato.clienteNome || '')}${html(doc)}<br>${html(fmtDataIso(de))} a ${html(fmtDataIso(ate))}<br>Emitido em ${html(new Date().toLocaleString('pt-BR'))}</div>
    </header>
    <section class="summary">
      <div><span>Saldo anterior</span><strong>${html(money(extrato.saldoAnterior))}</strong></div>
      <div><span>Saldo final</span><strong>${html(money(extrato.saldoFinal))}</strong></div>
      <div><span>Limite</span><strong>${html(money(extrato.limite))}</strong></div>
      <div><span>Disponível</span><strong>${html(money(extrato.disponivel))}</strong></div>
    </section>
    <table><thead><tr>
      <th>Data</th><th>Tipo</th><th>Descrição</th>
      <th class="right">Débito</th><th class="right">Crédito</th><th class="right">Saldo</th>
    </tr></thead><tbody>${rows || '<tr><td colspan="6">Nenhum movimento neste período.</td></tr>'}</tbody></table>
    <footer>ZenGestão · ${(extrato.itens || []).length} movimentos</footer>
    </body></html>`);
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 300);
}

export default function ConvenioPage() {
  const navigate = useNavigate();
  const { estabelecimentoId } = useEstabelecimento();
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteIdParam = searchParams.get('clienteId') || '';
  const permissions = useMemo(() => getAuthPermissoes() || {}, []);
  const podeReceber = permissions.movimentaConvenio !== false;
  const podeExcluirRecebimento = Boolean(permissions.excluiRecebimentoConvenio);
  const {
    showSuccess,
    showApiError,
    showError,
    askConfirm,
    setConfirmLoading,
    FeedbackUi,
  } = useFeedbackAlert();

  const [clienteId, setClienteId] = useState(clienteIdParam);
  const [clientes, setClientes] = useState([]);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [saldo, setSaldo] = useState(null);
  const [movimentos, setMovimentos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const [carregando, setCarregando] = useState(false);

  const [dlgAberto, setDlgAberto] = useState(false);
  const [formas, setFormas] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [recebendo, setRecebendo] = useState(false);
  const [formRec, setFormRec] = useState({
    valor: '',
    formaPagamentoId: '',
    sessaoId: '',
  });
  const [extratoAberto, setExtratoAberto] = useState(false);
  const [presetExtrato, setPresetExtrato] = useState('30');
  const [deExtrato, setDeExtrato] = useState('');
  const [ateExtrato, setAteExtrato] = useState('');
  const [telefoneExtrato, setTelefoneExtrato] = useState('');
  const [gerandoExtrato, setGerandoExtrato] = useState(false);
  const [enviandoExtrato, setEnviandoExtrato] = useState(false);
  const [waConectado, setWaConectado] = useState(false);

  const usuarioId = Number(getAuthUserId() || 0);

  useEffect(() => {
    const id = Number(estabelecimentoId);
    if (!id) {
      setWaConectado(false);
      return undefined;
    }
    let ativo = true;
    getWhatsAppSessao(id)
      .then(({ data }) => {
        if (!ativo) return;
        setWaConectado(data?.configurado !== false && data?.status === 'conectado');
      })
      .catch(() => {
        if (ativo) setWaConectado(false);
      });
    return () => { ativo = false; };
  }, [estabelecimentoId]);

  const abrirExtrato = () => {
    const cliente = clientes.find((c) => String(c.id) === String(clienteId));
    setTelefoneExtrato(cliente?.telefone || '');
    setPresetExtrato('30');
    setExtratoAberto(true);
    if (!cliente?.telefone && clienteId) {
      getClientById(clienteId)
        .then(({ data }) => setTelefoneExtrato(data?.telefone || ''))
        .catch(() => {});
    }
  };

  const gerarExtratoPdf = async () => {
    const { de, ate } = periodoPreset(presetExtrato, deExtrato, ateExtrato);
    if (!de || !ate || ate < de) {
      showError('Informe um período válido para o extrato.');
      return;
    }
    const popup = window.open('', '_blank');
    if (!popup) {
      showError('O navegador bloqueou a janela de impressão. Libere pop-ups deste site.');
      return;
    }
    setGerandoExtrato(true);
    try {
      const [{ data }, company] = await Promise.all([
        getConvenioExtrato(clienteId, { de, ate }),
        loadReportCompany(),
      ]);
      imprimirExtrato(popup, { company, extrato: data, de, ate });
    } catch (e) {
      popup.close();
      showApiError(e);
    } finally {
      setGerandoExtrato(false);
    }
  };

  const enviarExtratoWhatsApp = async () => {
    const { de, ate } = periodoPreset(presetExtrato, deExtrato, ateExtrato);
    if (!de || !ate || ate < de) {
      showError('Informe um período válido para o extrato.');
      return;
    }
    if (!estabelecimentoId) {
      showError('Selecione a filial conectada ao WhatsApp.');
      return;
    }
    setEnviandoExtrato(true);
    try {
      await enviarConvenioExtratoWhatsApp(clienteId, {
        de,
        ate,
        telefone: telefoneExtrato,
        estabelecimentoId: Number(estabelecimentoId),
      });
      showSuccess('Extrato enviado no WhatsApp do cliente.');
    } catch (e) {
      showApiError(e);
    } finally {
      setEnviandoExtrato(false);
    }
  };

  const carregarClientes = useCallback(async (termo = '') => {
    const { data } = await getAllClients({ indice: 1, qtde: 50, termo });
    setClientes(data.items || []);
  }, []);

  const carregarSaldo = useCallback(async (id) => {
    if (!id) {
      setSaldo(null);
      return;
    }
    const { data } = await getConvenioSaldo(id);
    setSaldo(data);
  }, []);

  const carregarMovimentos = useCallback(async (id, page = 1) => {
    if (!id) {
      setMovimentos([]);
      return;
    }
    const { data } = await getConvenioMovimentos({
      indice: page,
      qtde: 20,
      clienteId: id,
    });
    setMovimentos(data.items || []);
    setTotalPaginas(data.totalPages || 1);
  }, []);

  const recarregar = useCallback(async () => {
    if (!clienteId) return;
    setCarregando(true);
    setErro('');
    try {
      await Promise.all([
        carregarSaldo(clienteId),
        carregarMovimentos(clienteId, pagina),
      ]);
    } catch (e) {
      setErro(e?.response?.data?.error || e.message || 'Erro ao carregar convênio.');
    } finally {
      setCarregando(false);
    }
  }, [carregarMovimentos, carregarSaldo, clienteId, pagina]);

  useEffect(() => {
    carregarClientes(buscaCliente);
  }, [buscaCliente, carregarClientes]);

  useEffect(() => {
    if (clienteIdParam && clienteIdParam !== clienteId) {
      setClienteId(clienteIdParam);
    }
  }, [clienteIdParam, clienteId]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const sessoesAbertas = useMemo(
    () => (sessoes || []).filter((s) => !s.dataFechamento),
    [sessoes]
  );

  const formasRecebimento = useMemo(
    () => (formas || []).filter((f) => f.ativo !== false && !f.convenio && !f.troca),
    [formas]
  );

  const abrirRecebimento = async () => {
    setErro('');
    setOk('');
    if (!podeReceber) {
      showError('Seu perfil não tem permissão para movimentar convênio.');
      return;
    }
    try {
      const [fp, sc] = await Promise.all([
        getAllPaymentMethods(),
        getSessoesCaixa(),
      ]);
      setFormas(Array.isArray(fp.data) ? fp.data : fp.data?.items || []);
      setSessoes(Array.isArray(sc.data) ? sc.data : sc.data?.items || []);
      setFormRec({ valor: '', formaPagamentoId: '', sessaoId: '' });
      setDlgAberto(true);
    } catch (e) {
      setErro(e?.response?.data?.error || 'Não foi possível abrir o recebimento.');
    }
  };

  const confirmarRecebimento = async () => {
    setRecebendo(true);
    setErro('');
    try {
      if (!usuarioId) throw new Error('Usuário da sessão não identificado. Faça login novamente.');
      const valor = Number(String(formRec.valor).replace(',', '.'));
      if (!valor || valor <= 0) throw new Error('Informe um valor válido.');
      if (!formRec.formaPagamentoId) throw new Error('Selecione a forma de pagamento.');
      if (!formRec.sessaoId) throw new Error('Selecione uma sessão de caixa aberta.');

      await createConvenioRecebimento({
        clienteId: Number(clienteId),
        usuarioId,
        formaPagamentoId: Number(formRec.formaPagamentoId),
        sessaoId: Number(formRec.sessaoId),
        valor,
      });
      setDlgAberto(false);
      setOk('Recebimento lançado.');
      await recarregar();
    } catch (e) {
      setErro(e?.response?.data?.error || e.message || 'Erro ao receber.');
    } finally {
      setRecebendo(false);
    }
  };

  const excluirRecebimento = async (mov) => {
    if (!podeExcluirRecebimento) {
      showError('Seu perfil não tem permissão para excluir recebimento de convênio.');
      return;
    }

    const okConfirm = await askConfirm({
      title: 'Excluir recebimento',
      message: `Deseja excluir o recebimento de ${money(mov?.valor)} em ${fmtDate(mov?.data)}?\nEsta ação não pode ser desfeita.`,
      confirmLabel: 'Sim',
      cancelLabel: 'Não',
      confirmColor: 'error',
    });
    if (!okConfirm) return;

    setConfirmLoading(true);
    try {
      await deleteConvenioRecebimento(mov.id);
      showSuccess('Recebimento excluído.');
      setOk('Recebimento excluído.');
      await recarregar();
    } catch (e) {
      showApiError(e, 'Erro ao excluir recebimento.');
      setErro(e?.response?.data?.error || 'Erro ao excluir.');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <FeedbackUi />
      <PageHeader
        title="Convênio"
        subtitle="Lançamentos e baixas de contas a receber de clientes."
      />

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Buscar cliente"
              value={buscaCliente}
              onChange={(e) => setBuscaCliente(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Cliente</InputLabel>
              <Select
                label="Cliente"
                value={clienteId}
                onChange={(e) => {
                  const id = String(e.target.value);
                  setClienteId(id);
                  setPagina(1);
                  setSearchParams(id ? { clienteId: id } : {});
                }}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {clientes.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.nomeFantasia || c.razaoSocial} (#{c.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              disabled={!clienteId}
              onClick={() => navigate(`/edit-client/${clienteId}`)}
              sx={{ textTransform: 'none' }}
            >
              Editar cliente
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {saldo && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={800}>{saldo.clienteNome}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip label={`Limite: ${money(saldo.limite)}`} />
                <Chip
                  color={saldo.saldo < 0 ? 'error' : 'success'}
                  label={`Saldo: ${money(saldo.saldo)}`}
                />
                <Chip
                  color="primary"
                  label={`Disponível (Saldo + Limite): ${money(saldo.disponivel)}`}
                />
                {saldo.bloqueado && (
                  <Chip color="error" label={saldo.msgBloqueio || 'Bloqueado'} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Saldo = recebimentos − lançamentos (verde = crédito · vermelho = cliente deve)
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ReceiptLongIcon />}
                onClick={abrirExtrato}
                disabled={carregando}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Gerar extrato
              </Button>
              <Button
                variant="contained"
                startIcon={<AddCardIcon />}
                onClick={abrirRecebimento}
                disabled={carregando || !podeReceber}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Receber
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movimentos.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {clienteId ? 'Nenhum movimento.' : 'Selecione um cliente.'}
                </TableCell>
              </TableRow>
            )}
            {movimentos.map((m) => (
              <TableRow key={`${m.tipo}-${m.id}`}>
                <TableCell data-label="Data">{fmtDate(m.data)}</TableCell>
                <TableCell data-label="Tipo">
                  <Chip
                    size="small"
                    color={m.tipo === 'L' ? 'warning' : m.tipo === 'C' ? 'info' : 'success'}
                    label={
                      m.tipo === 'L'
                        ? 'Lançamento (venda)'
                        : m.tipo === 'C'
                          ? 'Cashback'
                          : String(m.observacao || '')
                                .toLowerCase()
                                .includes('devolu')
                            ? 'Devolução'
                            : 'Recebimento'
                    }
                  />
                </TableCell>
                <TableCell align="right" data-label="Valor">{money(m.valor)}</TableCell>
                <TableCell align="right" data-actions="">
                  {m.tipo === 'R' && podeExcluirRecebimento && (
                    <Tooltip title="Excluir recebimento">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => excluirRecebimento(m)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {clienteId && totalPaginas > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={totalPaginas}
            page={pagina}
            onChange={(_, p) => setPagina(p)}
            color="primary"
          />
        </Stack>
      )}

      <Dialog open={dlgAberto} onClose={() => setDlgAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Receber convênio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {sessoesAbertas.length === 0 && (
              <Alert severity="warning">
                Nenhuma sessão de caixa aberta. Abra o caixa no PDV antes de receber.
              </Alert>
            )}
            <TextField
              label="Valor"
              value={formRec.valor}
              onChange={(e) => setFormRec((p) => ({ ...p, valor: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Forma de pagamento</InputLabel>
              <Select
                label="Forma de pagamento"
                value={formRec.formaPagamentoId}
                onChange={(e) => setFormRec((p) => ({ ...p, formaPagamentoId: e.target.value }))}
              >
                {formasRecebimento.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>{f.descricao}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Sessão de caixa</InputLabel>
              <Select
                label="Sessão de caixa"
                value={formRec.sessaoId}
                onChange={(e) => setFormRec((p) => ({ ...p, sessaoId: e.target.value }))}
              >
                {sessoesAbertas.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    #{s.id} — PDV {s.pdvId} — {fmtDate(s.dataAbertura)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgAberto(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={confirmarRecebimento}
            disabled={recebendo || sessoesAbertas.length === 0}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={extratoAberto} onClose={() => setExtratoAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Extrato de convênio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={presetExtrato}
              onChange={(_, v) => { if (v) setPresetExtrato(v); }}
            >
              <ToggleButton value="30">30 dias</ToggleButton>
              <ToggleButton value="90">3 meses</ToggleButton>
              <ToggleButton value="365">1 ano</ToggleButton>
              <ToggleButton value="custom">Personalizado</ToggleButton>
            </ToggleButtonGroup>
            {presetExtrato === 'custom' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="De"
                  type="date"
                  value={deExtrato}
                  onChange={(e) => setDeExtrato(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Até"
                  type="date"
                  value={ateExtrato}
                  onChange={(e) => setAteExtrato(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            )}
            {waConectado && (
              <TextField
                label="WhatsApp do cliente"
                value={telefoneExtrato}
                onChange={(e) => setTelefoneExtrato(e.target.value)}
                fullWidth
                helperText="DDD + número. Usado só no envio pelo WhatsApp."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtratoAberto(false)}>Fechar</Button>
          {waConectado && (
            <Button
              startIcon={<WhatsAppIcon />}
              onClick={enviarExtratoWhatsApp}
              disabled={enviandoExtrato || gerandoExtrato}
              sx={{ textTransform: 'none' }}
            >
              WhatsApp
            </Button>
          )}
          <Button
            variant="contained"
            onClick={gerarExtratoPdf}
            disabled={gerandoExtrato || enviandoExtrato}
          >
            Gerar PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
